import React, { useEffect, useState } from 'react';
import {
  Badge, Button, DataTable, IdCell, Input, KV, Modal, PageHead, SearchBox,
  StatCard, Tabs, Textarea, dateStr, dateTimeStr, inr, num, titleCase,
} from '../components/ui';
import useList from '../hooks/useList';
import { financeApi } from '../api';
import { useToast } from '../context/ToastContext';

const Finance = () => {
  const toast = useToast();
  const [tab, setTab] = useState('credit');
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [detail, setDetail] = useState(null);
  const [review, setReview] = useState(null);
  const [form, setForm] = useState({ approvedAmount: '', interestRate: '12', tenureDays: '30', installments: '1', remarks: '' });
  const [busy, setBusy] = useState(false);

  const credit = useList(financeApi.creditRequests, { status: '', search: '' });
  const txns = useList(financeApi.transactions, { status: '' }, { auto: false });
  const escrow = useList(financeApi.escrow, {}, { auto: false });

  useEffect(() => {
    financeApi.stats().then(res => setStats(res.data)).catch(() => {});
    financeApi.partners().then(res => setPartners(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'transactions') txns.reload();
    if (tab === 'escrow') escrow.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const decide = async status => {
    setBusy(true);
    try {
      await financeApi.review(review._id, {
        status,
        approvedAmount: Number(form.approvedAmount) || undefined,
        interestRate: Number(form.interestRate) || 0,
        tenureDays: Number(form.tenureDays) || undefined,
        installments: Number(form.installments) || 1,
        remarks: form.remarks,
      });
      toast.success(`Credit request ${status}`);
      setReview(null);
      setDetail(null);
      credit.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const creditColumns = [
    {
      key: 'requestNumber',
      label: 'Request',
      render: row => (
        <>
          <div className="cell-strong mono">{row.requestNumber}</div>
          <div className="cell-muted">{dateStr(row.createdAt)}</div>
        </>
      ),
    },
    { key: 'applicant', label: 'Applicant', render: row => <IdCell image={row.applicant?.logo} name={row.applicant?.name} sub={row.applicant?.code} size="sm" /> },
    { key: 'financier', label: 'Finance partner', render: row => row.financier?.name || <span className="text-muted">Unassigned</span> },
    { key: 'requestedAmount', label: 'Requested', align: 'right', render: row => <strong>{inr(row.requestedAmount, true)}</strong> },
    {
      key: 'approvedAmount',
      label: 'Sanctioned',
      align: 'right',
      render: row => (row.approvedAmount ? (
        <>
          <strong style={{ color: 'var(--success)' }}>{inr(row.approvedAmount, true)}</strong>
          <div className="cell-muted">{row.interestRate}% · {row.tenureDays}d</div>
        </>
      ) : <span className="text-muted">—</span>),
    },
    { key: 'status', label: 'Status', render: row => <Badge status={row.status} /> },
  ];

  const txnColumns = [
    { key: 'paymentNumber', label: 'Payment', render: row => <span className="mono cell-strong">{row.paymentNumber}</span> },
    { key: 'order', label: 'Order', render: row => <span className="mono text-sm">{row.order?.orderNumber || '—'}</span> },
    { key: 'buyer', label: 'Buyer', render: row => row.buyer?.name || '—' },
    { key: 'seller', label: 'Seller', render: row => row.seller?.name || '—' },
    { key: 'amount', label: 'Amount', align: 'right', render: row => <strong>{inr(row.amount)}</strong> },
    { key: 'mode', label: 'Mode', render: row => titleCase(row.mode) },
    { key: 'status', label: 'Status', render: row => <Badge status={row.status} /> },
    { key: 'createdAt', label: 'When', render: row => <span className="text-sm">{dateTimeStr(row.createdAt)}</span> },
  ];

  const escrowColumns = [
    { key: 'paymentNumber', label: 'Payment', render: row => <span className="mono cell-strong">{row.paymentNumber}</span> },
    { key: 'order', label: 'Order', render: row => <span className="mono text-sm">{row.order?.orderNumber}</span> },
    { key: 'buyer', label: 'From', render: row => row.buyer?.name || '—' },
    { key: 'seller', label: 'Held for', render: row => row.seller?.name || '—' },
    { key: 'amount', label: 'Amount held', align: 'right', render: row => <strong>{inr(row.amount)}</strong> },
    { key: 'orderStatus', label: 'Order status', render: row => <Badge status={row.order?.status} /> },
    { key: 'createdAt', label: 'Held since', render: row => <span className="text-sm">{dateStr(row.createdAt)}</span> },
  ];

  return (
    <>
      <PageHead title="Finance" subtitle="Credit lines, transaction monitoring and escrow settlement." />

      {stats && (
        <div className="stat-grid stagger mb-2">
          <StatCard accent label="Sanctioned credit" value={inr(stats.sanctionedAmount, true)} icon="🏦" foot={`${num(stats.totalRequests)} requests`} />
          <StatCard label="Utilised" value={inr(stats.utilisedAmount, true)} icon="◈" />
          <StatCard label="Pending requests" value={num(stats.creditRequests.requested)} icon="⏳" />
          <StatCard label="Receivables" value={inr(stats.receivables, true)} icon="📥" foot={`${num(stats.receivableOrders)} orders`} />
          <StatCard label="Held in escrow" value={inr(stats.escrowHeld, true)} icon="🔒" onClick={() => setTab('escrow')} />
        </div>
      )}

      <Tabs
        tabs={[
          { value: 'credit', label: 'Credit requests' },
          { value: 'transactions', label: 'Transactions' },
          { value: 'escrow', label: 'Escrow' },
          { value: 'partners', label: `Partners (${partners.length})` },
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === 'credit' && (
        <>
          <div className="filters">
            <SearchBox value={credit.filters.search} onChange={v => credit.setFilter('search', v)} placeholder="Search request number…" />
            <select className="select" style={{ width: 170 }} value={credit.filters.status} onChange={e => credit.setFilter('status', e.target.value)}>
              <option value="">Any status</option>
              <option value="requested">Requested</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <DataTable
            columns={creditColumns}
            rows={credit.rows}
            loading={credit.loading}
            meta={credit.meta}
            onPage={credit.setPage}
            onRowClick={async row => {
              try {
                setDetail((await financeApi.creditRequest(row._id)).data);
              } catch (err) {
                toast.error(err.message);
              }
            }}
          />
        </>
      )}

      {tab === 'transactions' && (
        <>
          <div className="filters">
            <select className="select" style={{ width: 170 }} value={txns.filters.status} onChange={e => txns.setFilter('status', e.target.value)}>
              <option value="">Any status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <DataTable columns={txnColumns} rows={txns.rows} loading={txns.loading} meta={txns.meta} onPage={txns.setPage} />
        </>
      )}

      {tab === 'escrow' && (
        <DataTable columns={escrowColumns} rows={escrow.rows} loading={escrow.loading} meta={escrow.meta} onPage={escrow.setPage} />
      )}

      {tab === 'partners' && (
        <div className="grid-2 stagger">
          {partners.map(p => (
            <div className="card" key={p._id}>
              <div className="card-body">
                <IdCell image={p.logo} name={p.name} sub={p.code} size="lg" />
                <p className="text-sm text-muted mt-2">{p.description || 'No description provided.'}</p>
              </div>
            </div>
          ))}
          {partners.length === 0 && <p className="text-muted">No verified finance partners have joined yet.</p>}
        </div>
      )}

      <Modal
        open={!!detail}
        size="wide"
        title={detail?.requestNumber}
        subtitle={detail?.applicant?.name}
        onClose={() => setDetail(null)}
        footer={
          detail?.status === 'requested' && (
            <Button
              onClick={() => {
                setReview(detail);
                setForm({
                  approvedAmount: String(detail.requestedAmount),
                  interestRate: '12',
                  tenureDays: String(detail.tenureDays),
                  installments: '1',
                  remarks: '',
                });
              }}
            >
              Review request
            </Button>
          )
        }
      >
        {detail && (
          <>
            <div className="row mb-2"><Badge status={detail.status} /></div>
            <KV
              items={[
                { label: 'Applicant', value: `${detail.applicant?.name} (${detail.applicant?.code})` },
                { label: 'Applicant GSTIN', value: detail.applicant?.gstNumber || '—' },
                { label: 'Contact', value: detail.applicantUser ? `${detail.applicantUser.name} · ${detail.applicantUser.mobile}` : '—' },
                { label: 'Requested', value: <strong>{inr(detail.requestedAmount)}</strong> },
                { label: 'Tenure', value: `${detail.tenureDays} days` },
                { label: 'Purpose', value: detail.purpose || '—' },
                detail.order && { label: 'Against order', value: detail.order.orderNumber },
                { label: 'Applicant history', value: `${num(detail.applicant?.totalOrders)} orders · ${inr(detail.applicant?.totalBusiness)} business` },
                { label: 'Existing credit limit', value: inr(detail.applicant?.creditLimit) },
                detail.approvedAmount ? { label: 'Sanctioned', value: `${inr(detail.approvedAmount)} at ${detail.interestRate}%` } : null,
                detail.riskGrade && { label: 'Risk grade', value: detail.riskGrade },
                detail.remarks && { label: 'Remarks', value: detail.remarks },
              ]}
            />

            {(detail.repayments || []).length > 0 && (
              <>
                <div className="divider" />
                <div className="card-title mb-2">Repayment schedule</div>
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th style={{ textAlign: 'right' }}>Amount</th>
                        <th>Due date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.repayments.map((r, index) => (
                        <tr key={r._id}>
                          <td>{index + 1}</td>
                          <td style={{ textAlign: 'right' }} className="cell-strong">{inr(r.amount)}</td>
                          <td>{dateStr(r.dueDate)}</td>
                          <td><Badge status={r.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </Modal>

      <Modal
        open={!!review}
        title="Review credit request"
        subtitle={review?.requestNumber}
        onClose={() => setReview(null)}
        footer={
          <>
            <Button variant="danger" loading={busy} onClick={() => decide('rejected')}>Reject</Button>
            <Button loading={busy} onClick={() => decide('approved')}>Approve & sanction</Button>
          </>
        }
      >
        <p className="text-muted mb-2">
          Approving raises the applicant's platform credit limit and generates the repayment schedule.
        </p>
        <div className="grid-2">
          <Input label="Sanctioned amount (₹)" type="number" value={form.approvedAmount} onChange={e => setForm({ ...form, approvedAmount: e.target.value })} />
          <Input label="Interest rate (%)" type="number" step="0.1" value={form.interestRate} onChange={e => setForm({ ...form, interestRate: e.target.value })} />
          <Input label="Tenure (days)" type="number" value={form.tenureDays} onChange={e => setForm({ ...form, tenureDays: e.target.value })} />
          <Input label="Installments" type="number" min={1} value={form.installments} onChange={e => setForm({ ...form, installments: e.target.value })} />
        </div>
        <Textarea label="Remarks (sent to the applicant)" value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} />
      </Modal>
    </>
  );
};

export default Finance;
